import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Lock, ShieldCheck, ArrowUpRight, CircleNotch, Check } from "@phosphor-icons/react";
import PageShell from "@/components/site/PageShell";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useCart } from "@/context/CartContext";
import { formatUSD } from "@/data/products";
import { submitOrder } from "@/lib/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const inputCls =
  "rounded-none h-11 border-white/15 bg-transparent px-4 text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-primary";

const Field = ({ label, required, children, className = "" }) => (
  <div className={`flex flex-col gap-1.5 ${className}`}>
    <label className="mono-label text-muted-foreground">
      {label} {required && <span className="text-primary">*</span>}
    </label>
    {children}
  </div>
);

export default function Checkout() {
  const { items, subtotal, count, clear } = useCart();
  const navigate = useNavigate();
  const [f, setF] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "US",
    po_number: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [orderNumber, setOrderNumber] = useState(null);
  const set = (k) => (e) => setF((v) => ({ ...v, [k]: e.target.value }));

  useEffect(() => {
    if (items.length === 0 && !orderNumber) navigate("/cart");
  }, [items.length, navigate, orderNumber]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (f.name.trim().length < 2) return toast.error("Please enter your name.");
    if (!EMAIL_RE.test(f.email)) return toast.error("Please enter a valid email.");
    if (f.phone.trim().length < 5) return toast.error("Please enter a phone number.");
    if (f.address_line1.trim().length < 3) return toast.error("Please enter a shipping address.");
    if (!f.city.trim() || !f.state.trim() || !f.postal_code.trim())
      return toast.error("City, state and postal code are required.");
    if (items.length === 0) return toast.error("Your cart is empty.");

    setLoading(true);
    try {
      const payload = {
        ...f,
        items: items.map((i) => ({
          sku: i.sku,
          name: i.name,
          price: i.price,
          qty: i.qty,
          line_total: Math.round(i.price * i.qty * 100) / 100,
        })),
        subtotal,
      };
      const res = await submitOrder(payload);
      setOrderNumber(res.order_number);
      clear();
      toast.success(`Order ${res.order_number} received.`);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not place order. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  if (orderNumber) {
    return (
      <PageShell>
        <section className="bg-background py-20 md:py-32" data-testid="checkout-success">
          <div className="mx-auto max-w-3xl px-5 md:px-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="border border-primary/40 bg-secondary/40 p-10 md:p-16 text-center"
            >
              <div className="w-14 h-14 mx-auto border border-primary flex items-center justify-center mb-6">
                <Check size={28} weight="bold" className="text-primary" />
              </div>
              <span className="mono-label text-primary">/ Order received</span>
              <h1 className="mt-4 font-display font-black uppercase tracking-tighter text-4xl md:text-6xl">
                Thank you.
              </h1>
              <p className="mt-6 text-foreground/70 max-w-md mx-auto leading-relaxed">
                Your order is in the queue. Our sales desk will email a confirmed invoice
                (with freight, taxes and payment link) within 1 business day.
              </p>
              <div className="mt-8 inline-flex items-baseline gap-3 border border-white/15 px-6 py-4">
                <span className="mono-label text-muted-foreground">Order #</span>
                <span
                  className="font-mono-tech text-2xl text-primary"
                  data-testid="checkout-order-number"
                >
                  {orderNumber}
                </span>
              </div>
              <div className="mt-10 flex flex-wrap gap-4 justify-center">
                <Link
                  to="/"
                  className="border border-white/20 mono-label px-6 py-3 hover:border-primary hover:text-primary transition-colors"
                >
                  Back to home
                </Link>
                <Link
                  to="/inventory"
                  className="bg-primary text-primary-foreground px-6 py-3 mono-label hover:bg-foreground hover:text-background transition-colors inline-flex items-center gap-2"
                >
                  Continue shopping <ArrowUpRight size={16} weight="bold" />
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <section className="bg-background py-10 md:py-16" data-testid="checkout-page">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <div className="flex items-baseline gap-4 mb-8">
            <span className="mono-label text-primary">/ Checkout</span>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Lock size={14} weight="fill" className="text-primary" />
              <span className="mono-label text-[0.65rem]">HTTPS · SSL secured</span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <form
              onSubmit={onSubmit}
              className="lg:col-span-8 border border-white/10 p-6 md:p-10"
              data-testid="checkout-form"
            >
              <h2 className="font-display font-bold uppercase tracking-tight text-2xl md:text-3xl mb-8">
                Buyer &amp; Shipping
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Full name" required>
                  <Input className={inputCls} value={f.name} onChange={set("name")} data-testid="co-name" />
                </Field>
                <Field label="Company">
                  <Input className={inputCls} value={f.company} onChange={set("company")} data-testid="co-company" />
                </Field>
                <Field label="Email" required>
                  <Input type="email" className={inputCls} value={f.email} onChange={set("email")} data-testid="co-email" />
                </Field>
                <Field label="Phone" required>
                  <Input className={inputCls} value={f.phone} onChange={set("phone")} data-testid="co-phone" />
                </Field>
                <Field label="Address line 1" required className="md:col-span-2">
                  <Input className={inputCls} value={f.address_line1} onChange={set("address_line1")} data-testid="co-addr1" />
                </Field>
                <Field label="Address line 2" className="md:col-span-2">
                  <Input className={inputCls} value={f.address_line2} onChange={set("address_line2")} data-testid="co-addr2" />
                </Field>
                <Field label="City" required>
                  <Input className={inputCls} value={f.city} onChange={set("city")} data-testid="co-city" />
                </Field>
                <Field label="State / Province" required>
                  <Input className={inputCls} value={f.state} onChange={set("state")} data-testid="co-state" />
                </Field>
                <Field label="Postal / ZIP" required>
                  <Input className={inputCls} value={f.postal_code} onChange={set("postal_code")} data-testid="co-zip" />
                </Field>
                <Field label="Country">
                  <Input className={inputCls} value={f.country} onChange={set("country")} data-testid="co-country" />
                </Field>
                <Field label="PO / Reference #" className="md:col-span-2">
                  <Input className={inputCls} value={f.po_number} onChange={set("po_number")} data-testid="co-po" />
                </Field>
                <Field label="Order notes" className="md:col-span-2">
                  <Textarea
                    className={`${inputCls} h-auto min-h-[100px] py-3`}
                    value={f.notes}
                    onChange={set("notes")}
                    placeholder="Delivery preferences, spec notes, etc."
                    data-testid="co-notes"
                  />
                </Field>
              </div>

              <div className="mt-8 p-4 border border-white/10 bg-secondary/40 flex items-start gap-3">
                <ShieldCheck size={22} weight="light" className="text-primary shrink-0 mt-0.5" />
                <p className="text-sm text-foreground/60 leading-relaxed">
                  <span className="text-foreground font-medium">Secure checkout.</span>{" "}
                  This page is served over HTTPS with an active SSL certificate. We do
                  not store any card details on this site — invoicing is handled by our
                  sales desk after order confirmation.
                </p>
              </div>

              <motion.button
                type="submit"
                disabled={loading || items.length === 0}
                whileTap={{ scale: 0.98 }}
                className="mt-8 w-full md:w-auto inline-flex items-center justify-center gap-3 bg-primary text-primary-foreground px-8 py-4 mono-label hover:bg-foreground hover:text-background transition-colors disabled:opacity-60"
                data-testid="checkout-submit"
              >
                {loading ? (
                  <>
                    Placing order
                    <CircleNotch size={18} weight="bold" className="animate-spin" />
                  </>
                ) : (
                  <>
                    <Lock size={16} weight="bold" />
                    Place Secure Order — {formatUSD(subtotal)}
                    <ArrowUpRight size={16} weight="bold" />
                  </>
                )}
              </motion.button>
            </form>

            <aside
              className="lg:col-span-4 border border-white/10 p-6 md:p-8 h-fit lg:sticky lg:top-24"
              data-testid="checkout-summary"
            >
              <p className="mono-label text-primary mb-4">/ Order summary</p>
              <div className="max-h-80 overflow-auto divide-y divide-white/10 border-y border-white/10">
                {items.map((i) => (
                  <div key={i.sku} className="py-3 flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-sm truncate">{i.name}</p>
                      <p className="mono-label text-muted-foreground mt-0.5">
                        {i.sku} · qty {i.qty}
                      </p>
                    </div>
                    <span className="font-mono-tech text-sm shrink-0">
                      {formatUSD(i.price * i.qty)}
                    </span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between py-3 mt-2">
                <span className="mono-label text-muted-foreground">Items</span>
                <span className="font-mono-tech">{count}</span>
              </div>
              <div className="flex justify-between py-3 border-t border-white/10">
                <span className="mono-label text-muted-foreground">Subtotal</span>
                <span className="font-mono-tech text-xl text-primary">
                  {formatUSD(subtotal)}
                </span>
              </div>
              <p className="mono-label text-muted-foreground mt-4 leading-relaxed text-[0.65rem]">
                Freight, taxes and payment terms will be confirmed on your invoice.
              </p>
            </aside>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
